import React, { useEffect, useRef, useState } from "react";

const TechTicker = ({
  items = [],
  speed = 80,
  gap = 28,
  height = 88,
  ariaLabel = "Technology ticker",
  pauseOnHover = false,
  edgeFade = true,
}) => {
  const trackRef = useRef(null);
  const [trackWidth, setTrackWidth] = useState(0);

  useEffect(() => {
    const measure = () => {
      if (!trackRef.current) return;
      const w = trackRef.current.offsetWidth;
      if (w > 0) setTrackWidth(w);
    };

    const t = setTimeout(measure, 120);
    const ro = new ResizeObserver(measure);
    if (trackRef.current) ro.observe(trackRef.current);
    window.addEventListener("load", measure);

    return () => {
      clearTimeout(t);
      ro.disconnect();
      window.removeEventListener("load", measure);
    };
  }, [items, gap]);

  const durationSec =
    trackWidth > 0 ? Math.max(trackWidth / Math.max(30, speed), 8) : 20;

  return (
    <div
      aria-label={ariaLabel}
      role="marquee"
      aria-live="off"
      className={[
        "relative w-full overflow-hidden",
        edgeFade
          ? "[mask-image:linear-gradient(90deg,transparent_0%,black_8%,black_92%,transparent_100%)]"
          : "",
      ].join(" ")}
      style={{ height }}
    >
      <style>{`
        @keyframes ticker-infinite {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .ticker-belt {
          display: flex;
          align-items: center;
          width: max-content;
          will-change: transform;
          animation: ticker-infinite linear infinite;
        }
        .ticker-belt:hover {
          animation-play-state: ${pauseOnHover ? "paused" : "running"};
        }
      `}</style>

      <div className="flex h-full items-center">
        <div
          className="ticker-belt"
          style={{ animationDuration: `${durationSec}s` }}
        >
          {/* 
            Each row has paddingLeft = gap/2 and paddingRight = gap/2.
            This means: [gap/2] chip [gap] chip [gap] ... chip [gap/2]
            Two rows back-to-back = gap/2 + gap/2 = gap at the seam. Perfect.
          */}
          <TrackRow ref={trackRef} items={items} gap={gap} />
          <TrackRow items={items} gap={gap} />
        </div>
      </div>
    </div>
  );
};

const TrackRow = React.forwardRef(function TrackRow({ items, gap }, ref) {
  const half = gap / 2;

  return (
    <div
      ref={ref}
      className="flex items-center shrink-0"
      style={{
        gap,
        paddingLeft: half,
        paddingRight: half,
      }}
    >
      {items.map((it, i) => {
        const chip = (
          <div
            className={[
              "inline-flex items-center gap-2 rounded-full px-4 py-2.5 shrink-0",
              "bg-zinc-100 border border-zinc-200 ring-1 ring-black/5",
              "dark:bg-white/10 dark:border-white/10 dark:ring-white/5",
              "shadow-[0_2px_12px_rgba(0,0,0,.06)] dark:shadow-[0_2px_12px_rgba(0,0,0,.25)]",
              "hover:bg-zinc-50 dark:hover:bg-white/15 transition-colors",
            ].join(" ")}
          >
            <span className="shrink-0 flex items-center">{it.icon}</span>
            <span className="text-[13px] font-medium leading-none text-zinc-800 dark:text-white/90 whitespace-nowrap">
              {it.label}
            </span>
          </div>
        );

        return (
          <div key={`${it.label}-${i}`} className="shrink-0">
            {it.href ? (
              <a
                href={it.href}
                target="_blank"
                rel="noreferrer"
                aria-label={it.label}
                className="focus:outline-none rounded-full focus-visible:ring-2 focus-visible:ring-indigo-500/60"
              >
                {chip}
              </a>
            ) : (
              chip
            )}
          </div>
        );
      })}
    </div>
  );
});

export default TechTicker;